import type { DecideFollowUp, FollowUp } from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { refreshLists } from '../../lib/list';
import { useSession } from '../../lib/session';

const key = (t: string | undefined, id: string) => ['assessments', t, id] as const;

/** AI follow-up questions on an assessment's answers (task 87). */
export function useFollowUps(id: string, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id, id), 'follow-ups'],
    queryFn: () => api<FollowUp[]>('GET', `/assessments/${id}/follow-ups`),
    enabled: !!tenant && enabled,
  });
}

/** A confirmed change moves the answer and the score, so the whole assessment reloads. */
function useFollowUpMutation<I, O>(id: string, fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: fn,
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: key(tenant?.id, id) }), refreshLists(qc)]),
  });
}

export type FollowUpsRun = { proposed: number; failed: boolean; follow_ups: FollowUp[] };

export const useProposeFollowUps = (id: string) =>
  useFollowUpMutation(id, () => api<FollowUpsRun>('POST', `/assessments/${id}/follow-ups/propose`));
export const useReplyFollowUp = (id: string) =>
  useFollowUpMutation(id, ({ fid, text }: { fid: string; text: string }) => api<FollowUp>('POST', `/assessments/${id}/follow-ups/${fid}/reply`, { text })); // prettier-ignore
export const useDecideFollowUp = (id: string) =>
  useFollowUpMutation(id, ({ fid, ...body }: DecideFollowUp & { fid: string }) => api<FollowUp>('POST', `/assessments/${id}/follow-ups/${fid}/decide`, body)); // prettier-ignore
